// Game model types
export enum GameStatus {
  LOBBY = 'lobby',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed'
}

export enum EntryType {
  TEXT = 'text',
  DRAWING = 'drawing'
}

// Helper type to enforce the sequence
export type NextEntryType = {
  [EntryType.TEXT]: EntryType.DRAWING;
  [EntryType.DRAWING]: EntryType.TEXT;
};

export interface Game {
  id: string;
  key: string;
  title: string | null;
  hostId: string | null;
  status: GameStatus;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  players: Player[];
  stacks: Stack[];
  playerQueues: PlayerQueue[];
  totalTurnsPerPlayer: number;  // Equal to number of players
  totalEntriesPerStack: number; // Equal to number of players
  updatedAt: string;
  playerOrder: string[];
  discordIntegration?: DiscordIntegration;
}

export interface DiscordIntegration {
  guildId: string;
  channelId: string;
  recapThreadId?: string | null;
  recapPosted?: boolean;
}

// Player model types
export interface Player {
  id: string;
  gameId: string;
  name: string;
  avatarUrl: string | null;
  avatarThumbUrl?: string | null;
  discordUserId?: string | null;
  isHost: boolean;
  joinedAt: string;
  lastNotifiedAt: string | null;
  orderIndex: number | null;
  turnsRemaining: number;     // Tracks how many more turns this player has
  turnsCompleted: number;     // Tracks how many turns this player has completed
  handwritingFont?: string;   // User's chosen handwriting font
}

// PlayerQueue model types
export interface PlayerQueue {
  id: string;
  playerId: string;
  gameId: string;
  stackIds: string[];
  stacks: Stack[];
  size: number;
}

// Stack model types
export interface Stack {
  id: string;
  gameId: string;
  originPlayerId: string;
  assignedQueueId?: string;   // Queue this stack is assigned to
  activePlayerId?: string;    // Player currently working on this stack
  status: 'pending' | 'active' | 'completed';
  isComplete: boolean;        // True when entries.length equals totalEntriesPerStack
  createdAt: string;
  updatedAt: string;
  entries: StackEntry[];
  entriesRemaining: number;   // How many more entries needed to complete the stack
  progress: number;           // Percentage of completion (0-100)
  expectedEntries: ExpectedEntry[];
}

// StackEntry model types
export interface StackEntry {
  id: string;
  stackId: string;
  playerId: string;
  entryType: EntryType;
  content: string;
  contentOpaque?: string | null;
  timestamp: string;
  orderIndex: number;         // 1-based index in the stack (1 = initial prompt, 2 = first drawing, etc.)
  isLastEntry: boolean;       // True if this is the last entry needed for this stack
  createdAt: string;
}

// Helper function to determine the next entry type
export const getNextEntryType = (currentType: EntryType): EntryType => {
  return currentType === EntryType.TEXT ? EntryType.DRAWING : EntryType.TEXT;
};

// Helper function to determine the expected entry type based on entry count
export const getExpectedEntryType = (stack: Stack, playerId: string): EntryType | null => {
  const nextEntry = stack.expectedEntries.find(entry => entry.expectedPlayerId === playerId && !entry.completed);
  return nextEntry ? nextEntry.entryType : null;
};

// Helper function to calculate stack completion
export const calculateStackProgress = (stack: Stack, totalEntries: number): number => {
  return Math.floor((stack.entries.length / totalEntries) * 100);
};

// Helper function to check if a player has completed all their turns
export const hasPlayerCompletedAllTurns = (player: Player): boolean => {
  return player.turnsRemaining === 0;
};

// Helper function to check if a stack is complete
export const isStackComplete = (stack: Stack): boolean => {
  if (!stack.expectedEntries || stack.expectedEntries.length === 0) {
    return false;
  }
  return stack.expectedEntries.every(entry => entry.completed);
};

// NotificationLog model types
export enum NotificationType {
  TURN_NOTIFICATION = 'turn_notification',
  GAME_STARTED = 'game_started',
  GAME_COMPLETED = 'game_completed'
}

export enum NotificationStatus {
  SENT = 'sent',
  FAILED = 'failed'
}

export interface NotificationLog {
  id: string;
  playerId: string;
  gameId: string;
  stackId: string | null;
  notificationType: NotificationType;
  sentAt: string;
  status: NotificationStatus;
  errorMessage: string | null;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  error?: string;
}

export interface ExpectedEntry {
  orderIndex: number;
  expectedPlayerId: string;
  entryType: EntryType;
  completed: boolean;
}

// Helper function to get the next expected entry for a stack
export const getNextExpectedEntry = (stack: Stack): ExpectedEntry | null => {
  if (!stack.expectedEntries || stack.expectedEntries.length === 0) {
    console.warn(`Stack ${stack.id} has no expectedEntries`);
    return null;
  }
  return stack.expectedEntries.find(entry => !entry.completed) || null;
};

// Helper function to check if a player can submit to a stack
export const canSubmitToStack = (stack: Stack, playerId: string): boolean => {
  if (!stack.expectedEntries) {
    return false;
  }
  const nextEntry = getNextExpectedEntry(stack);
  return nextEntry !== null && nextEntry.expectedPlayerId === playerId;
}; 
